/**
 * The one way this SPA talks to a server.
 *
 * PATHS ARE ALWAYS RELATIVE. The browser only ever addresses its own origin;
 * package 3b's BFF decides what leaves it. There is no base URL, no
 * VITE_API_BASE_URL and no way to configure one -- an absolute URL is refused
 * here and an API hostname literal is an ESLint error. Phase 1c deleted
 * exactly this chain from the scanner; it is not being rebuilt.
 *
 * NO TENANT ID EVER LEAVES THIS CLIENT. Tenant scope is the session's, decided
 * server-side (api/src/adminRoutes.ts) and asserted per route by the isolation
 * suite. A call that appears to need one is reading the wrong endpoint.
 *
 * ERRORS ARE VALUES. Every non-2xx becomes an ApiError carrying status, the
 * server's `error` code, a classified `kind` and the FULL body -- because
 * several conflicts ship payload the UI must render (already_assigned's
 * existingStandort, order_mismatch's expected, tenant_busy's runningType).
 */

import type * as T from './types';

export type ApiErrorKind =
  | 'auth'         // 401: the session is gone. Terminal; never retried.
  | 'forbidden'    // 403 forbidden: the role is insufficient. Also a bug signal.
  | 'validation'   // 400: the request was wrong. Field-level message.
  | 'conflict'     // 409: the world was different. Actionable message + payload.
  | 'notFound'     // 404: the row is gone. Re-fetch the list.
  | 'upstream'     // 502: Google/eBay/DATEV failed. Retryable, not the owner's fault.
  | 'unavailable'  // 503: a feature or config state. A named screen, not a toast.
  | 'config'       // 500: the platform is misconfigured. An operator must act.
  | 'deployment'   // bad_origin / bff_unconfigured: the two Workers disagree.
  | 'network'      // the request never completed.
  | 'unknown';

/**
 * The two codes whose STATUS does not determine their kind, because both name
 * a mismatch between the admin Worker and the API Worker rather than anything
 * the owner did (package 3b's runbook section 1).
 */
const DEPLOYMENT_CODES = new Set(['bad_origin', 'bff_unconfigured']);

export function classifyStatus(status: number, code: string | null): ApiErrorKind {
  if (code && DEPLOYMENT_CODES.has(code)) return 'deployment';
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 400) return 'validation';
  if (status === 409) return 'conflict';
  if (status === 500) return 'config';
  if (status === 502) return 'upstream';
  if (status === 503) return 'unavailable';
  return 'unknown';
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly kind: ApiErrorKind;
  readonly body: Record<string, unknown>;

  constructor(status: number, code: string | null, body: Record<string, unknown>, kind?: ApiErrorKind) {
    // The message is for developers and logs. Never rendered: user-facing text
    // comes from de.json keyed error.<code> (lib/i18n.ts).
    super(`${status} ${code ?? 'no-code'}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
    this.kind = kind ?? classifyStatus(status, code);
  }
}

export interface AdminFetchInit extends Omit<RequestInit, 'body'> {
  /** Serialized as JSON and given the Content-Type. Use instead of `body`. */
  json?: unknown;
}

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export async function adminFetchRaw(
  path: string,
  init: AdminFetchInit = {},
  fetchImpl: FetchLike = ((p, i) => fetch(p, i)) as FetchLike,
): Promise<Response> {
  if (!path.startsWith('/')) {
    throw new Error(`adminFetch takes a relative path; got ${JSON.stringify(path)}`);
  }
  const { json, headers, ...rest } = init;
  const merged: Record<string, string> = { Accept: 'application/json', ...(headers as Record<string, string> | undefined) };
  let body: string | undefined;
  if (json !== undefined) {
    merged['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  }
  try {
    return await fetchImpl(path, {
      // Stated rather than defaulted: 'same-origin' is already the default,
      // and writing it is what stops a later refactor from setting 'omit' and
      // silently dropping the session cookie from every request.
      credentials: 'same-origin',
      ...rest,
      headers: merged,
      ...(body === undefined ? {} : { body }),
    });
  } catch (cause) {
    throw new ApiError(0, null, { cause: String(cause) }, 'network');
  }
}

export async function adminFetch<T>(
  path: string,
  init: AdminFetchInit = {},
  fetchImpl?: FetchLike,
): Promise<T> {
  const res = await adminFetchRaw(path, init, fetchImpl);
  if (res.status === 204) return null as T;

  // Content-Type first: a misrouted request can return an HTML error page, and
  // crashing on JSON.parse would replace "keine Verbindung" with a stack trace.
  const isJson = (res.headers.get('Content-Type') ?? '').includes('application/json');
  let body: Record<string, unknown> = {};
  if (isJson) {
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      body = {};
    }
  }

  if (!res.ok) {
    const code = typeof body.error === 'string' ? body.error : null;
    throw new ApiError(res.status, code, body);
  }
  return body as T;
}

/**
 * The typed surface, one function per row of ADMIN_ROUTE_TABLE
 * (api/src/adminRoutes.ts:138-175) that a browser session can call. The two
 * provider callbacks are absent by construction: they carry minRole null,
 * land on the API's PUBLIC hostname, and are reached by a redirect from the
 * provider -- never by this client.
 *
 * Every path here is a literal. No template interpolation of anything but an
 * opaque id, and ids are encoded.
 */
const enc = encodeURIComponent;

export const ADMIN_API = {
  me: () => adminFetch<T.MeResponse>('/admin-api/me'),
  overview: () => adminFetch<T.OverviewResponse>('/admin-api/overview'),

  listStaff: () => adminFetch<{ staff: T.Staff[] }>('/admin-api/staff'),
  createStaff: (json: { displayName: string; pin: string; role?: T.StaffRole }) =>
    adminFetch<{ staff: T.Staff }>('/admin-api/staff', { method: 'POST', json }),
  updateStaff: (id: string, json: { role?: T.StaffRole; status?: T.RowStatus; pin?: string }) =>
    adminFetch<{ staff: T.Staff }>(`/admin-api/staff/${enc(id)}`, { method: 'PATCH', json }),

  listUsers: () => adminFetch<{ users: T.AdminUser[] }>('/admin-api/users'),
  inviteUser: (json: { email: string; role: 'manager' }) =>
    adminFetch<{ user: T.AdminUser }>('/admin-api/users', { method: 'POST', json }),
  updateUser: (id: string, json: { status: T.RowStatus }) =>
    adminFetch<{ user: T.AdminUser }>(`/admin-api/users/${enc(id)}`, { method: 'PATCH', json }),

  listLocations: () => adminFetch<{ locations: T.Location[] }>('/admin-api/locations'),
  createLocation: (json: { name: string; barcode?: string | null }) =>
    adminFetch<{ location: T.Location }>('/admin-api/locations', { method: 'POST', json }),
  updateLocation: (id: string, json: { name?: string; barcode?: string | null }) =>
    adminFetch<{ location: T.Location }>(`/admin-api/locations/${enc(id)}`, { method: 'PATCH', json }),
  deleteLocation: (id: string) =>
    adminFetch<{ ok: true; id: string }>(`/admin-api/locations/${enc(id)}`, { method: 'DELETE' }),

  listLists: () => adminFetch<{ lists: T.ListEntry[] }>('/admin-api/lists'),
  createList: (json: { name: string }) =>
    adminFetch<{ list: T.ListEntry }>('/admin-api/lists', { method: 'POST', json }),
  // MUST stay above renameList in any mental model of the route table: the
  // server registers /lists/reorder before /lists/:id for the same reason
  // (adminRoutes.ts:590-594).
  reorderLists: (order: string[]) =>
    adminFetch<{ lists: T.ListEntry[] }>('/admin-api/lists/reorder', { method: 'PATCH', json: { order } }),
  renameList: (id: string, json: { name: string }) =>
    adminFetch<{ list: T.ListEntry }>(`/admin-api/lists/${enc(id)}`, { method: 'PATCH', json }),
  deleteList: (id: string) =>
    adminFetch<{ ok: true; id: string }>(`/admin-api/lists/${enc(id)}`, { method: 'DELETE' }),

  getSettings: () => adminFetch<T.SettingsResponse>('/admin-api/settings'),
  putSettings: (settings: Record<string, string>) =>
    adminFetch<T.SettingsResponse>('/admin-api/settings', { method: 'PUT', json: { settings } }),
  patchFeatures: (json: { ebay?: boolean; priceScan?: boolean }) =>
    adminFetch<T.FeaturesResponse>('/admin-api/features', { method: 'PATCH', json }),

  googleStart: () => adminFetch<T.OAuthStart>('/admin-api/google/oauth-start', { method: 'POST' }),
  googleFinish: (state: string) =>
    adminFetch<T.GoogleFinish>('/admin-api/google/oauth-finish', { method: 'POST', json: { state } }),
  pickerToken: () => adminFetch<T.PickerToken>('/admin-api/google/picker-token', { method: 'POST' }),

  getSheet: () => adminFetch<{ sheet: T.SheetDetail | null }>('/admin-api/sheet'),
  connectSheet: (spreadsheetId: string) =>
    adminFetch<{ sheet: T.SheetDetail & { tabs: string[] } }>('/admin-api/sheet/connect', { method: 'POST', json: { spreadsheetId } }),
  validateSheet: () =>
    adminFetch<{ sheet: T.SheetDetail & { createdTabs: string[] } }>('/admin-api/sheet/validate', { method: 'POST' }),
  createSheetFromTemplate: (json: { title?: string }) =>
    adminFetch<{ sheet: T.SheetDetail }>('/admin-api/sheet/create-from-template', { method: 'POST', json }),

  getEbay: () => adminFetch<{ connection: T.EbayConnection | null; features: T.Features }>('/admin-api/ebay'),
  ebayStart: () => adminFetch<T.OAuthStart>('/admin-api/ebay/oauth-start', { method: 'POST' }),
  ebayFinish: (state: string) =>
    adminFetch<T.EbayFinish>('/admin-api/ebay/oauth-finish', { method: 'POST', json: { state } }),
  ebayDisconnect: () => adminFetch<T.EbayFinish>('/admin-api/ebay/disconnect', { method: 'POST' }),

  jobs: (limit?: number) =>
    adminFetch<T.JobsResponse>(`/admin-api/jobs${limit ? `?limit=${limit}` : ''}`),
  runAction: (action: string) =>
    adminFetch<{ jobId: string; status: T.JobStatus; error?: string | null; stats?: unknown }>(
      `/admin-api/actions/${enc(action)}`, { method: 'POST' }),
  /** DATEV returns a FILE, not JSON — hence Raw (adminRoutes.ts:1572-1579). */
  datevPreview: (since: string, until: string) =>
    adminFetchRaw(`/admin-api/actions/datev-export-preview?since=${enc(since)}&until=${enc(until)}`, { method: 'POST' }),

  metrics: (range: T.MetricRange) => adminFetch<T.MetricsBundle>(`/admin-api/metrics?range=${range}`),
} as const;

export const AUTH_API = {
  requestCode: (json: { email: string; turnstileToken: string }) =>
    adminFetch<{ message: string; code?: 'EMAIL_UNDELIVERABLE' }>('/auth/request', { method: 'POST', json }),
  verifyCode: (json: { email: string; code: string }) =>
    adminFetch<{ user: T.AuthSession['user'] }>('/auth/verify', { method: 'POST', json }),
  session: () => adminFetch<T.AuthSession>('/auth/session'),
  logout: () => adminFetch<{ ok: true }>('/auth/logout', { method: 'POST' }),
  listSessions: () => adminFetch<{ sessions: T.SessionEntry[] }>('/auth/sessions'),
  revokeSession: (sessionId: string) =>
    adminFetch<{ ok: true }>('/auth/sessions/revoke', { method: 'POST', json: { sessionId } }),
  pairToken: (label: string) =>
    adminFetch<T.PairingToken>('/auth/devices/pair-token', { method: 'POST', json: { label } }),
  listDevices: () => adminFetch<{ devices: T.DeviceRowRaw[] }>('/auth/devices'),
  revokeDevice: (deviceId: string) =>
    adminFetch<{ ok: true }>('/auth/devices/revoke', { method: 'POST', json: { deviceId } }),
} as const;
